// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';

import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import type { LogRoundCourse } from './types';

const ACK_TIMEOUT_MS = 12000;
const MEMBERSHIP_WAIT_MS = 5000;

type CreateRoundParams = {
  apiBaseUrl: string;
  token: string | null | undefined;
  hiddenTourName: string;
  playerId: string;
  scp: string;
  course: LogRoundCourse;
  holesCount: number;
  playDate: string;
};

export type CreateRoundResult = { roundId: string; tourId: string };

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Solo version of round-init.tsx's hidden-tour bootstrap: creates a hidden tour for just the
 * signed-in player, then initiates a round in it. Deliberately not extracted from round-init.tsx
 * (which owns its own, much larger multi-player version of this dance) — this is a smaller,
 * single-player-only reimplementation for the "log a round I already played" flow.
 */
export function useLogRoundCreation() {
  const createRound = useCallback((params: CreateRoundParams): Promise<CreateRoundResult> => {
    const { apiBaseUrl, token, hiddenTourName, playerId, scp, course, holesCount, playDate } = params;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(token ?? null);

    return new Promise((resolve, reject) => {
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      let roundRequestId: string | null = null;
      let retried = false;
      let tourIdForRetry: string | null = null;

      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        if (timeoutHandle) clearTimeout(timeoutHandle);
        unsubscribe();
        fn();
      };

      const armTimeout = (ms: number) => {
        if (timeoutHandle) clearTimeout(timeoutHandle);
        timeoutHandle = setTimeout(() => settle(() => reject(new Error('timeout'))), ms);
      };

      const sendRoundInitiate = (tourId: string) => {
        tourIdForRetry = tourId;
        roundRequestId = generateId();
        socket.send({
          type: 'round.initiate',
          requestId: roundRequestId,
          tourId,
          name: course.name,
          holesCount,
          playDate,
          players: [{ id: playerId, scp }],
          course: {
            id: course.id,
            name: course.name,
            address: course.address,
            lat: course.lat,
            lon: course.lon,
            selectedTeeId: course.selectedTeeId ?? null,
            selectedTeeName: course.selectedTeeName ?? null,
          },
          gameDefinitionIds: [],
        });
        armTimeout(ACK_TIMEOUT_MS);
      };

      const unsubscribe = socket.onMessage((message) => {
        const payload = message as Record<string, unknown>;
        const type = typeof payload.type === 'string' ? payload.type : null;

        if (type === 'tour.created') {
          if (payload.requestId !== tourRequestId) return;
          const tourId = (payload.tourId as string | undefined) ?? (payload.TourId as string | undefined) ?? null;
          if (!tourId || tourId === EMPTY_GUID) {
            settle(() => reject(new Error('missingTour')));
            return;
          }
          sendRoundInitiate(tourId);
          return;
        }

        if (type === 'round.created') {
          if (!roundRequestId || payload.requestId !== roundRequestId) return;
          const roundId = payload.roundId as string | undefined;
          const tourId = (payload.tourId as string | undefined) ?? tourIdForRetry;
          if (!roundId || !tourId) {
            settle(() => reject(new Error('missingRound')));
            return;
          }
          settle(() => resolve({ roundId, tourId }));
          return;
        }

        if (type === 'round.initiate.rejected') {
          if (!roundRequestId || payload.requestId !== roundRequestId) return;
          if (retried || !tourIdForRetry) {
            const rejectionMessage = (payload.message as string | undefined) ?? (payload.code as string | undefined) ?? 'rejected';
            settle(() => reject(new Error(rejectionMessage)));
            return;
          }

          // A player added to a hidden tour at creation time can momentarily lag behind as a
          // confirmed member. Ask for a membership snapshot once, then retry the initiate —
          // mirrors round-init.tsx's own guard against this race, trimmed to one player.
          retried = true;
          const membersRequestId = generateId();
          socket.send({ type: 'tour.members.request', requestId: membersRequestId, tourId: tourIdForRetry });
          armTimeout(MEMBERSHIP_WAIT_MS);
          const capturedTourId = tourIdForRetry;
          const retryTimeout = setTimeout(() => {
            if (settled) return;
            sendRoundInitiate(capturedTourId);
          }, MEMBERSHIP_WAIT_MS);
          // Any members snapshot for this tour is enough of a signal to retry sooner than the
          // membership-wait timeout — the snapshot's contents aren't otherwise needed here.
          const snapshotUnsubscribe = socket.onMessage((snapshotMessage) => {
            const snapshotPayload = snapshotMessage as Record<string, unknown>;
            const snapshotType = typeof snapshotPayload.type === 'string' ? snapshotPayload.type : null;
            if (snapshotType !== 'tour.members.snapshot') return;
            const snapshotTourId =
              (snapshotPayload.tourId as string | undefined) ?? (snapshotPayload.TourId as string | undefined);
            if (snapshotTourId && snapshotTourId !== capturedTourId) return;
            clearTimeout(retryTimeout);
            snapshotUnsubscribe();
            if (!settled) {
              sendRoundInitiate(capturedTourId);
            }
          });
          return;
        }
      });

      const tourRequestId = generateId();
      armTimeout(ACK_TIMEOUT_MS);
      const sent = socket.send({
        type: 'tour.initiate',
        requestId: tourRequestId,
        tourName: hiddenTourName,
        playerIds: [playerId],
        isHidden: true,
      });
      if (sent === 'dropped') {
        settle(() => reject(new Error('dropped')));
      }
    });
  }, []);

  return { createRound };
}
